import { useEffect, useMemo, useRef, useState } from "react";
import { AtSign } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AppCard,
  AppCardBody,
  AppFieldGroup,
  AppIconButton,
  AppInput,
  AppText,
  AppTextArea,
} from "../../ui-foundation";
import {
  appendMentionTrigger,
  getTrailingMentionQuery,
  insertMentionSuggestion,
  retainMentionDraftsForText,
  type MentionDraft,
  type MentionSuggestion,
} from "../../shared/community";

interface MentionComposerFieldProps {
  value: string;
  onChangeText: (text: string) => void;
  mentions: MentionDraft[];
  onMentionsChange: (mentions: MentionDraft[]) => void;
  lookupCandidates: (query: string) => Promise<MentionSuggestion[]>;
  label?: string;
  helper?: string;
  placeholder?: string;
  multiline?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  fieldProps?: Record<string, unknown>;
  inputProps?: Record<string, unknown>;
}

const MentionComposerField = ({
  value,
  onChangeText,
  mentions,
  onMentionsChange,
  lookupCandidates,
  label,
  helper,
  placeholder,
  multiline = false,
  disabled = false,
  autoFocus = false,
  fieldProps,
  inputProps,
}: MentionComposerFieldProps) => {
  const [suggestions, setSuggestions] = useState<MentionSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const lookupCandidatesRef = useRef(lookupCandidates);
  lookupCandidatesRef.current = lookupCandidates;

  const activeQuery = useMemo(() => getTrailingMentionQuery(value, mentions), [mentions, value]);
  const activeQueryText = activeQuery?.query;

  useEffect(() => {
    let isMounted = true;

    if (activeQueryText === undefined || disabled) {
      setSuggestions([]);
      setLoading(false);
      return () => {
        isMounted = false;
      };
    }

    setLoading(true);
    const timeoutId = window.setTimeout(async () => {
      try {
        const nextSuggestions = await lookupCandidatesRef.current(activeQueryText);
        if (isMounted) {
          setSuggestions(nextSuggestions);
        }
      } catch {
        if (isMounted) {
          setSuggestions([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }, 120);

    return () => {
      isMounted = false;
      window.clearTimeout(timeoutId);
    };
  }, [activeQueryText, disabled]);

  const handleChange = (nextValue: string) => {
    onChangeText(nextValue);
    onMentionsChange(retainMentionDraftsForText(nextValue, mentions));
  };

  const handleSelect = (suggestion: MentionSuggestion) => {
    const nextState = insertMentionSuggestion(value, mentions, suggestion);
    onChangeText(nextState.text);
    onMentionsChange(nextState.mentions);
    setSuggestions([]);
  };

  const handleStartMention = () => {
    if (activeQuery) {
      return;
    }

    onChangeText(appendMentionTrigger(value));
  };

  return (
    <AppFieldGroup label={label} helper={helper} {...(fieldProps as any)}>
      {multiline ? (
        <AppTextArea
          value={value}
          onChangeText={handleChange}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          {...(inputProps as any)}
        />
      ) : (
        <AppInput
          value={value}
          onChangeText={handleChange}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          {...(inputProps as any)}
        />
      )}

      <div className="flex justify-end">
        <AppIconButton
          label="Tag someone"
          title="Tag someone"
          tone="ghost"
          size="sm"
          disabled={disabled}
          onPress={handleStartMention}
        >
          <AtSign size={17} />
        </AppIconButton>
      </div>

      {activeQuery ? (
        <AppCard accessibilityRole="list" accessibilityLabel="Member suggestions">
          <AppCardBody py="$xs">
            {loading ? (
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                Looking for members...
              </AppText>
            ) : suggestions.length === 0 ? (
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                No matching members yet.
              </AppText>
            ) : (
              <div className="space-y-1">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.id}
                    type="button"
                    aria-label={`Tag ${suggestion.displayName}`}
                    onClick={() => handleSelect(suggestion)}
                    className="flex w-full items-center gap-3 rounded-2xl px-2 py-2 text-left transition-colors hover:bg-muted"
                  >
                    <Avatar className="h-9 w-9 shrink-0">
                      {suggestion.avatarUrl ? <AvatarImage src={suggestion.avatarUrl} alt={suggestion.displayName} /> : null}
                      <AvatarFallback className="bg-primary/15 text-primary text-xs font-semibold">
                        {suggestion.displayName[0]?.toUpperCase() ?? "K"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <AppText fontSize="$sm" lineHeight="$sm">
                        {suggestion.displayName}
                      </AppText>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </AppCardBody>
        </AppCard>
      ) : null}
    </AppFieldGroup>
  );
};

export default MentionComposerField;
